import { useState, useEffect, useMemo } from 'react';
import useSearchCandidates from '../api/useSearchCandidates';
import useFetchCandidateBoards from '../api/useFetchCandidateBoards';
import Input from '@/components/Input';
import { Button } from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import { Tag, type TagColor } from '@/components/Tag';
import Select from '@/components/Select';
import Message from '@/components/Message';
import { showSuccessToast, showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import { FiMoreVertical } from 'react-icons/fi';
import { cn } from '@/lib/utils';

type JobPostInfo = {
  id: number;
  companyName: string;
  position: string;
  location: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  jobPosts: JobPostInfo[];
  onSubmit: (candidateIds: number[]) => Promise<void>;
  excludeCandidateIds?: number[];
  onSuccess?: () => void;
  isSharedJobPost?: boolean;
};

type ModalStep = 'search' | 'confirm';

export default function SuggestCandidatesModal({
  isOpen,
  onClose,
  jobPosts,
  onSubmit,
  excludeCandidateIds = [],
  onSuccess,
  isSharedJobPost = false,
}: Props) {
  const [step, setStep] = useState<ModalStep>('search');
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<number[]>([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedBoardId, setSelectedBoardId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: boardsData } = useFetchCandidateBoards();
  const { data, isLoading } = useSearchCandidates({
    keyword: searchKeyword,
    boardId: selectedBoardId ? Number(selectedBoardId) : undefined,
    excludeJobPostId: jobPosts.length > 0 && jobPosts[0].id > 0 ? jobPosts[0].id : undefined,
  });

  // 提案済み求職者を除外
  const candidates = useMemo(() => {
    const allCandidates = data?.candidates || [];
    return allCandidates.filter((candidate) => !excludeCandidateIds.includes(candidate.id));
  }, [data?.candidates, excludeCandidateIds]);

  const handleToggleCandidate = (candidateId: number) => {
    setSelectedCandidateIds((prev) =>
      prev.includes(candidateId) ? prev.filter((id) => id !== candidateId) : [...prev, candidateId]
    );
  };

  const handleSubmit = async () => {
    if (selectedCandidateIds.length === 0) return;

    setIsSubmitting(true);
    try {
      await onSubmit(selectedCandidateIds);
      showSuccessToast('求職者への提案が完了しました');
      handleClose();
      // 成功時に親のモーダルも閉じるためのコールバック
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Failed to suggest candidates:', error);
      showErrorToast(error instanceof Error ? error.message : '求職者への提案に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep('search');
    setSelectedCandidateIds([]);
    setSearchKeyword('');
    setSelectedBoardId('');
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setStep('search');
      setSelectedCandidateIds([]);
      setSearchKeyword('');
      setSelectedBoardId('');
    }
  }, [isOpen]);

  const selectedCandidates = candidates.filter((c) => selectedCandidateIds.includes(c.id));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={handleClose} />
        <div
          className={cn(
            'relative rounded-sm bg-white shadow-xl',
            step === 'search' ? 'w-[860px]' : 'w-[640px]'
          )}
        >
          <div className="flex items-center justify-between border-b border-solid border-border px-lg py-md">
            <h2 className="text-heading-xs">求職者へ提案</h2>
          </div>
          {step === 'search' ? (
            <>
              <div className="px-6 py-4">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Input
                      type="text"
                      placeholder="キーワードで検索"
                      value={searchKeyword}
                      onChange={(e) => setSearchKeyword(e.target.value)}
                      search
                    />
                  </div>
                  <div className="w-48">
                    <Select
                      items={[
                        { value: 'all', label: 'すべてのステータス' },
                        ...(boardsData?.records || []).map((board) => ({
                          value: board.id.toString(),
                          label: board.title,
                        })),
                      ]}
                      value={selectedBoardId || 'all'}
                      onValueChange={(value) => setSelectedBoardId(value === 'all' ? '' : value)}
                      defaultLabel="すべてのステータス"
                    />
                  </div>
                </div>
              </div>
              <div className="h-[50vh] overflow-y-auto border-y border-gray-200 px-6 py-4">
                {isLoading ? (
                  <div className="flex h-full items-center justify-center">
                    <p className="text-gray-500">読み込み中...</p>
                  </div>
                ) : candidates.length === 0 ? (
                  <div className="flex h-full items-center justify-center">
                    <p className="text-gray-500">候補者が見つかりません</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-max min-w-full">
                      <tbody>
                        {candidates.map((candidate) => (
                          <tr key={candidate.id} className={'border-t border-solid border-border'}>
                            <td className="sticky left-0 z-10 bg-white px-3 py-xs shadow-[inset_-1px_0_0_0_#e5e7eb]">
                              <div className="flex items-center gap-3">
                                <Checkbox
                                  checked={selectedCandidateIds.includes(candidate.id)}
                                  onChange={() => handleToggleCandidate(candidate.id)}
                                />
                                <div className="flex items-center gap-2">
                                  <span className="text-heading-xxs">
                                    {candidate.name}
                                    {candidate.age && ` (${candidate.age})`}
                                  </span>
                                  {candidate.organization_users?.[0] && (
                                    <Tag size="sm">{candidate.organization_users[0].name}</Tag>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-sm py-xs">
                              <div
                                className="max-w-[150px] truncate text-body-xs text-main"
                                title={candidate.company_name || undefined}
                              >
                                {candidate.company_name}
                              </div>
                            </td>
                            <td className="px-sm py-xs">
                              <div className="max-w-[150px] min-w-[30px] truncate text-body-xs whitespace-nowrap text-main">
                                {candidate.job_position}
                              </div>
                            </td>
                            <td className="px-sm py-xs">
                              <div className="flex flex-nowrap gap-sm">
                                {candidate.tags.map((tag) => (
                                  <Tag key={tag.id} color={tag.color_hex as TagColor} size="sm">
                                    {tag.name}
                                  </Tag>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between px-6 py-4">
                <p className="text-sm text-gray-500">{selectedCandidateIds.length}件選択中</p>
                <div className="flex gap-3">
                  <Button type="button" onClick={handleClose} color="secondary" size="md">
                    キャンセル
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setStep('confirm')}
                    disabled={selectedCandidateIds.length === 0}
                    color="primary"
                    size="md"
                  >
                    確認する
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-lg px-6 py-8">
                {isSharedJobPost && (
                  <Message color="caution" className="mb-md">
                    <p className="text-sm">シェア求人を提案すると自社求人へ複製されます。</p>
                  </Message>
                )}
                <p className="text-center text-body-md">以下の求職者にこの求人を提案しますか？</p>
                <div className="flex flex-col items-center justify-center">
                  <div className="text-left text-heading-xxs">
                    {jobPosts.slice(0, 5).map((jobPost, index) => (
                      <div key={jobPost.id}>
                        ・{jobPost.companyName} / {jobPost.position} / {jobPost.location}
                        {index === 4 && jobPosts.length > 5 && <>＋{jobPosts.length - 5}件</>}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="m-[0_auto] flex w-fit max-w-[360px] min-w-[160px] flex-col gap-xs rounded-sm border border-solid border-border bg-white p-lg">
                  {selectedCandidates.slice(0, 5).map((candidate) => (
                    <div key={candidate.id} className="text-heading-xxs break-all">
                      {candidate.name}
                      {candidate.age && `(${candidate.age})`}
                    </div>
                  ))}
                  {selectedCandidates.length > 5 && (
                    <div className="flex items-center justify-center">
                      <FiMoreVertical />
                    </div>
                  )}
                </div>
              </div>

              {/* フッター */}
              <div className="flex justify-end gap-3 border-t border-solid border-border px-6 py-4">
                <Button type="button" onClick={() => setStep('search')} color="secondary" size="md">
                  戻る
                </Button>
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  color="primary"
                  size="md"
                >
                  {isSubmitting ? '提案中...' : '提案する'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
